import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Input, Textarea, Badge } from '@jaylabs/ui';
import Logo from "../components/Logo";
import { useAuth, computeBadge, computeLevel } from '../context/AuthContext';
import { API_URL } from '../utils/apiUrl';
import { showNotification } from '../components/ui/notification';
import ChatSidebar from "../components/ChatSidebar";
import UserBadgeBar from '../components/UserBadgeBar';
import './Tasks.css';

function Tasks() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters — persisted to localStorage so they stick across navigation
  const [filterCategory, setFilterCategory] = useState(() => localStorage.getItem('tf_category') || '');
  const [filterTag, setFilterTag] = useState(() => localStorage.getItem('tf_tag') || '');
  const [filterProject, setFilterProject] = useState(() => localStorage.getItem('tf_project') || '');
  const [filterPriority, setFilterPriority] = useState(() => localStorage.getItem('tf_priority') || '');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('tf_sortBy') || 'priority');
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem('tf_sortOrder') || 'DESC');
  const [showCompleted, setShowCompleted] = useState(() => localStorage.getItem('tf_showCompleted') === 'true');
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  // View mode
  const [viewMode, setViewMode] = useState(localStorage.getItem('tasksViewMode') || 'detailed');

  // AI Breakdown Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownResult, setBreakdownResult] = useState(null);
  const [breakdownError, setBreakdownError] = useState('');

  // Edit Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    points: 10,
    difficulty: 'medium',
    priority: 'normal',
    category: 'personal',
    project_id: '',
    due_date: '',
    tags: []
  });
  const [projects, setProjects] = useState([]);
  const [editTagInput, setEditTagInput] = useState('');
  const [notesByTask, setNotesByTask] = useState({});
  const [openNotes, setOpenNotes] = useState({});
  const [noteInputs, setNoteInputs] = useState({});
  const [noteAttachments, setNoteAttachments] = useState({});
  const [notesLoading, setNotesLoading] = useState({});
  const [showBolt, setShowBolt] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [overwhelmMode, setOverwhelmMode] = useState(false);
  const [overwhelmIndex, setOverwhelmIndex] = useState(0);
  const [resumeOverwhelmAfterEdit, setResumeOverwhelmAfterEdit] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkProject, setBulkProject] = useState('');
  const [bulkDueDate, setBulkDueDate] = useState('');
  const [bulkDifficulty, setBulkDifficulty] = useState('');
  const [bulkPoints, setBulkPoints] = useState('');
  const [bulkTagsInput, setBulkTagsInput] = useState('');
  const [categories, setCategories] = useState([]);
  const [expandedTasks, setExpandedTasks] = useState({});
  const [subtasksByTask, setSubtasksByTask] = useState({});
  const [subtasksLoading, setSubtasksLoading] = useState({});
  const [dragOverSubtask, setDragOverSubtask] = useState(null);
  const [selectedSubtask, setSelectedSubtask] = useState(null);
  const dragSubtaskRef = useRef(null);
  const editDueDateRef = useRef(null);

  const storedBg = localStorage.getItem('dashboardBg') || '/backgrounds/Background-2-1024x682.jpg';

  const fetchProjects = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setProjects(data.projects);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Build query string
      const params = new URLSearchParams();
      if (filterCategory) params.append('category_id', filterCategory);
      if (filterTag) params.append('tag', filterTag);
      if (filterProject) params.append('project_id', filterProject);
      if (filterPriority) params.append('priority', filterPriority);
      params.append('sortBy', sortBy);
      params.append('order', sortOrder);
      params.append('is_completed', showCompleted);

      const response = await fetch(`${API_URL}/api/tasks?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setTasks(data.tasks);
      } else {
        setError(data.message || 'Failed to fetch tasks');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterTag, filterProject, filterPriority, sortBy, sortOrder, showCompleted]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages/inbox?unreadOnly=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setUnreadCount(data.unreadCount ?? 0);
      }
    } catch (err) {
      console.error('Error fetching unread messages:', err);
    }
  }, []);

  // Edit dialog opener
  const handleEditTask = useCallback((task) => {
    if (overwhelmMode) {
      setResumeOverwhelmAfterEdit(true);
      setOverwhelmMode(false);
    }
    setEditingTask(task);

    // Format due_date for datetime-local input
    let formattedDueDate = '';
    if (task.due_date) {
      const date = new Date(task.due_date);
      formattedDueDate = date.toISOString().slice(0, 16);
    }

    setEditForm({
      title: task.title,
      description: task.description || '',
      points: task.points,
      difficulty: task.difficulty,
      priority: task.priority || 'normal',
      category: task.category,
      category_id: task.category_id || '',
      status: task.status || 'pending',
      project_id: task.project_id || '',
      due_date: formattedDueDate,
      tags: task.tags || []
    });
    setEditTagInput('');
    setEditDialogOpen(true);
  }, [overwhelmMode]);
  useEffect(() => {
    fetchTasks();
    fetchUnreadCount();
    fetchProjects();
    fetchCategories();
  }, [fetchTasks, fetchProjects, fetchCategories, fetchUnreadCount]);

  // Open a specific task when navigated with state (e.g., from Dashboard)
  useEffect(() => {
    if (!loading && tasks.length && location.state?.openTaskId) {
      const targetTask = tasks.find(t => t.id === location.state.openTaskId);
      if (targetTask) {
        handleEditTask(targetTask);
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [loading, tasks, location.pathname, location.state, navigate, handleEditTask]);

  const fetchNotes = async (taskId) => {
    setNotesLoading(prev => ({ ...prev, [taskId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tasks/${taskId}/notes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setNotesByTask(prev => ({ ...prev, [taskId]: data.notes || [] }));
      }
    } catch (err) {
      console.error('Error fetching notes:', err);
    } finally {
      setNotesLoading(prev => ({ ...prev, [taskId]: false }));
    }
  };

  const toggleNotes = async (taskId) => {
    setOpenNotes(prev => {
      const next = { ...prev, [taskId]: !prev[taskId] };
      return next;
    });
    if (!notesByTask[taskId]) {
      await fetchNotes(taskId);
    }
  };

  const handleAddNote = async (taskId) => {
    const content = (noteInputs[taskId] || '').trim();
    if (!content) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tasks/${taskId}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content,
          attachment_url: (noteAttachments[taskId] || '').trim() || undefined
        })
      });
      const data = await response.json();
      if (response.ok) {
        setNotesByTask(prev => ({
          ...prev,
          [taskId]: [data.note, ...(prev[taskId] || [])]
        }));
        setNoteInputs(prev => ({ ...prev, [taskId]: '' }));
        setNoteAttachments(prev => ({ ...prev, [taskId]: '' }));
      }
    } catch (err) {
      console.error('Error adding note:', err);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return date.toLocaleString();
  };

  const handleCompleteTask = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tasks/${taskId}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh tasks list
        fetchTasks();
        showNotification('Task completed! 🎉', `You earned ${data.pointsEarned} points!`, 'success');;
        const earned = data.totalPointsEarned || data.pointsEarned || 0;
        if (updateUser) {
          const prevPoints = user?.total_points || 0;
          const newPoints = prevPoints + earned;
          const newBadge = computeBadge(newPoints);
          const oldLevel = computeLevel(prevPoints);
          const newLevel = computeLevel(newPoints);
          const oldBadge = computeBadge(prevPoints);
          updateUser({
            total_points: newPoints,
            badge: newBadge,
            level: newLevel
          });
          setShowBolt(true);
          setTimeout(() => setShowBolt(false), 1400);
          if (newBadge !== oldBadge || newLevel > oldLevel) {
            setShowFireworks(true);
            setTimeout(() => setShowFireworks(false), 2200);
          }
        }
      } else {
        showNotification('Error', data.message || 'Failed to complete task', 'error');
      }
    } catch (err) {
      showNotification('Error', 'Error connecting to server', 'error');
      console.error('Error:', err);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchTasks();
        showNotification('Deleted', 'Task deleted', 'info');
      } else {
        const data = await response.json();
        showNotification('Error', data.message || 'Failed to delete task', 'error');
      }
    } catch (err) {
      showNotification('Error', 'Error connecting to server', 'error');
      console.error('Error:', err);
    }
  };

  const handleAddEditTag = () => {
    if (editTagInput.trim() && !editForm.tags.includes(editTagInput.trim())) {
      setEditForm(prev => ({
        ...prev,
        tags: [...prev.tags, editTagInput.trim()]
      }));
      setEditTagInput('');
    }
  };

  const handleRemoveEditTag = (tagToRemove) => {
    setEditForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleEditTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEditTag();
    }
  };

  const handleUpdateTask = async () => {
    const wasOverwhelm = overwhelmMode || resumeOverwhelmAfterEdit;
    if (!editForm.title.trim()) {
      showNotification('Validation', 'Title is required!', 'warning');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tasks/${editingTask.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...editForm,
          priority: editForm.priority || 'normal'
        })
      });

      const data = await response.json();

      if (response.ok) {
        showNotification('Saved', 'Task updated successfully!', 'success');
        setEditDialogOpen(false);
        fetchTasks();
        if (wasOverwhelm) {
          setOverwhelmMode(true);
          setResumeOverwhelmAfterEdit(false);
        }
      } else {
        showNotification('Error', data.message || 'Failed to update task', 'error');
      }
    } catch (err) {
      showNotification('Error', 'Error connecting to server', 'error');
      console.error('Error:', err);
    }
  };

  useEffect(() => {
    if (chatOpen) {
      setUnreadCount(0);
    }
  }, [chatOpen]);

  const handleAIBreakdown = (task) => {
    setSelectedTask(task);
    setBreakdownResult(null);
    setBreakdownError('');
    setDialogOpen(true);
  };

  const requestAIBreakdown = async (mode = 'initial') => {
    if (!selectedTask) return;
    setBreakdownLoading(true);
    setBreakdownError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/tasks/${selectedTask.id}/breakdown`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ mode })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to generate breakdown');
      }
      setBreakdownResult(data.breakdown);
    } catch (err) {
      setBreakdownError(err.message || 'Error generating breakdown');
    } finally {
      setBreakdownLoading(false);
    }
  };

  const handleAcceptBreakdown = async () => {
    if (!selectedTask || !breakdownResult) return;
    setBreakdownLoading(true);
    setBreakdownError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/tasks/${selectedTask.id}/breakdown/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ breakdown: breakdownResult })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to create subtasks');
      }
      await fetchTasks();
      // Auto-expand the parent task to show new subtasks
      const parentId = selectedTask.id;
      setExpandedTasks(prev => ({ ...prev, [parentId]: true }));
      setSubtasksByTask(prev => ({ ...prev, [parentId]: data.subtasks }));
      setDialogOpen(false);
      setBreakdownResult(null);
    } catch (err) {
      setBreakdownError(err.message || 'Error creating subtasks');
    } finally {
      setBreakdownLoading(false);
    }
  };

  const fetchSubtasks = async (taskId) => {
    setSubtasksLoading(prev => ({ ...prev, [taskId]: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/tasks/${taskId}/subtasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setSubtasksByTask(prev => ({ ...prev, [taskId]: data.subtasks }));
      }
    } catch (err) {
      console.error('Error fetching subtasks:', err);
    } finally {
      setSubtasksLoading(prev => ({ ...prev, [taskId]: false }));
    }
  };

  const toggleSubtasks = (taskId) => {
    const isOpen = expandedTasks[taskId];
    setExpandedTasks(prev => ({ ...prev, [taskId]: !isOpen }));
    if (!isOpen && !subtasksByTask[taskId]) {
      fetchSubtasks(taskId);
    }
    if (isOpen) setSelectedSubtask(null);
  };

  const focusTask = (task) => {
    // Switch to detailed view and expand/focus this task
    setViewMode('detailed');
    localStorage.setItem('tasksViewMode', 'detailed');
    setExpandedTasks(prev => ({ ...prev, [task.id]: true }));
    if (parseInt(task.subtask_count) > 0 && !subtasksByTask[task.id]) {
      fetchSubtasks(task.id);
    }
  };

  const handleSubtaskComplete = async (parentId, subtaskId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tasks/${subtaskId}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        fetchSubtasks(parentId);
        showNotification('Step done! 🎉', `You earned ${data.pointsEarned} points!`, 'success');
        const earned = data.totalPointsEarned || data.pointsEarned || 0;
        if (updateUser) {
          const prevPoints = user?.total_points || 0;
          const newPoints = prevPoints + earned;
          updateUser({
            total_points: newPoints,
            badge: computeBadge(newPoints),
            level: computeLevel(newPoints)
          });
        }
      }
    } catch (err) {
      console.error('Error completing subtask:', err);
    }
  };

  const handleSubtaskReorder = async (parentId, orderedIds) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/tasks/${parentId}/subtasks/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ orderedIds })
      });
    } catch (err) {
      console.error('Error reordering subtasks:', err);
    }
  };

  const handleSubtaskDragStart = (subtask) => {
    dragSubtaskRef.current = subtask;
  };

  const handleSubtaskDrop = (parentId, targetSubtask) => {
    const dragged = dragSubtaskRef.current;
    if (!dragged || dragged.id === targetSubtask.id) return;
    const subtasks = subtasksByTask[parentId] || [];
    const reordered = subtasks.filter(s => s.id !== dragged.id);
    const targetIdx = reordered.findIndex(s => s.id === targetSubtask.id);
    reordered.splice(targetIdx, 0, dragged);
    setSubtasksByTask(prev => ({ ...prev, [parentId]: reordered }));
    handleSubtaskReorder(parentId, reordered.map(s => s.id));
    dragSubtaskRef.current = null;
    setDragOverSubtask(null);
  };

  const toggleSelectTask = (taskId) => {
    setSelectedTasks(prev =>
      prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
    );
  };

  const clearBulkForm = () => {
    setBulkCategory('');
    setBulkProject('');
    setBulkDueDate('');
    setBulkDifficulty('');
    setBulkPoints('');
    setBulkTagsInput('');
    setSelectedTasks([]);
  };

  const applyBulkUpdate = async () => {
    if (selectedTasks.length === 0) return;
    const updates = {};
    if (bulkCategory) {
      updates.category_id = bulkCategory;
      const catMatch = categories.find(c => String(c.id) === String(bulkCategory));
      if (catMatch) {
        updates.category = catMatch.name;
      }
    }
    if (bulkProject) updates.project_id = bulkProject;
    if (bulkDueDate) updates.due_date = bulkDueDate;
    if (bulkDifficulty) updates.difficulty = bulkDifficulty;
    if (bulkPoints !== '') updates.points = Number(bulkPoints);
    if (bulkTagsInput.trim()) {
      updates.tags = bulkTagsInput.split(',').map(t => t.trim()).filter(Boolean);
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/tasks/bulk-update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ taskIds: selectedTasks, updates })
      });
      const data = await res.json();
      if (res.ok) {
        fetchTasks();
        clearBulkForm();
      } else {
        showNotification('Error', data.message || 'Bulk update failed', 'error');
      }
    } catch (err) {
      showNotification('Error', 'Error performing bulk update', 'error');
      console.error(err);
    }
  };

  // Persist filters to localStorage whenever they change
  useEffect(() => { localStorage.setItem('tf_category', filterCategory); }, [filterCategory]);
  useEffect(() => { localStorage.setItem('tf_tag', filterTag); }, [filterTag]);
  useEffect(() => { localStorage.setItem('tf_project', filterProject); }, [filterProject]);
  useEffect(() => { localStorage.setItem('tf_priority', filterPriority); }, [filterPriority]);
  useEffect(() => { localStorage.setItem('tf_sortBy', sortBy); }, [sortBy]);
  useEffect(() => { localStorage.setItem('tf_sortOrder', sortOrder); }, [sortOrder]);
  useEffect(() => { localStorage.setItem('tf_showCompleted', showCompleted); }, [showCompleted]);

  const clearFilters = () => {
    setFilterCategory('');
    setFilterTag('');
    setFilterProject('');
    setFilterPriority('');
    setSortBy('priority');
    setSortOrder('DESC');
    setShowCompleted(false);
    ['tf_category','tf_tag','tf_project','tf_priority','tf_sortBy','tf_sortOrder','tf_showCompleted']
      .forEach(k => localStorage.removeItem(k));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get unique tags from all tasks
  const allTags = [...new Set(tasks.flatMap(task => task.tags || []))];
  const categoryNameById = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.id] = c.name; });
    return map;
  }, [categories]);
  const getCategoryLabel = useCallback((task) => {
    if (task.category_id && categoryNameById[task.category_id]) return categoryNameById[task.category_id];
    if (task.category) return task.category;
    return 'Uncategorized';
  }, [categoryNameById]);
  const priorityRank = (priority = 'normal') => {
    const val = String(priority || '').toLowerCase();
    if (val === 'critical') return 4;
    if (val === 'high') return 3;
    if (val === 'normal') return 2;
    if (val === 'medium') return 2;
    if (val === 'low') return 1;
    return 0;
  };
  const sortedOverwhelmTasks = useMemo(() => {
    const active = tasks.filter(t => !t.is_completed);
    return active.sort((a, b) => {
      const prDiff = priorityRank(b.priority) - priorityRank(a.priority);
      if (prDiff !== 0) return prDiff;
      const aDate = a.due_date ? new Date(a.due_date) : null;
      const bDate = b.due_date ? new Date(b.due_date) : null;
      if (aDate && bDate) return aDate - bDate;
      if (aDate) return -1;
      if (bDate) return 1;
      return (b.points || 0) - (a.points || 0);
    });
  }, [tasks]);
  const priorityColor = (priority = 'normal') => {
    const val = String(priority || '').toLowerCase();
    if (val === 'critical') return 'danger';
    if (val === 'high') return 'warning';
    if (val === 'medium') return 'warning';
    if (val === 'low') return 'success';
    return 'secondary';
  };
  const statusVariant = (status) => {
    if (status === 'completed') return 'success';
    return 'warning';
  };
  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      const prDiff = priorityRank(b.priority) - priorityRank(a.priority);
      if (prDiff !== 0) return prDiff;
      const aDate = a.due_date ? new Date(a.due_date) : null;
      const bDate = b.due_date ? new Date(b.due_date) : null;
      if (aDate && bDate) return aDate - bDate;
      if (aDate) return -1;
      if (bDate) return 1;
      return (b.points || 0) - (a.points || 0);
    });
  }, [tasks]);
  const allCategories = categories.length
    ? categories
    : [...new Set(tasks.map(t => t.category).filter(Boolean))].map(name => ({ id: name, name }));
  const currentOverwhelmTask = sortedOverwhelmTasks.length
    ? sortedOverwhelmTasks[overwhelmIndex % sortedOverwhelmTasks.length]
    : null;

  const handleNextOverwhelmTask = () => {
    if (!sortedOverwhelmTasks.length) return;
    setOverwhelmIndex((prev) => (prev + 1) % sortedOverwhelmTasks.length);
  };

  return (
    <div
      className="dd-tasks-page"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.7), rgba(255,255,255,0.7)), url(${storedBg})`
      }}
    >
      {showBolt && (
        <div className="dd-overlay-bolt">
          <span className="dd-overlay-bolt-text">
            ⚡⚡⚡
          </span>
        </div>
      )}
      {showFireworks && (
        <div className="dd-overlay-fireworks">
          <span className="dd-overlay-fireworks-text">
            🎆 🎇 🎉<br />🔥 YOU LEVELED UP! 🔥
          </span>
        </div>
      )}
      <div className="dd-container" style={{ transition: 'opacity 1.2s ease', opacity: overwhelmMode ? 0.1 : 1 }}>
        {/* Top Bar with Logo and shared badge/actions */}
        <div className="dd-hstack dd-justify-between dd-align-center dd-mb-6">
          <Logo size="xl" />
          <div className="dd-hstack dd-gap-2 dd-align-center">
            {localStorage.getItem('hideFriends') !== 'true' && (
              <button
                className="dd-icon-btn dd-icon-btn-lg"
                aria-label="Friends"
                title="Friends"
                onClick={() => navigate('/friends')}
              >
                <span aria-hidden="true">👥</span>
              </button>
            )}
            <button
              className="dd-icon-btn dd-icon-btn-lg"
              aria-label="Dashboard"
              title="Dashboard"
              onClick={() => navigate('/dashboard')}
            >
              <span aria-hidden="true">🏠</span>
            </button>
            {localStorage.getItem('hideFriends') !== 'true' && (
              <button
                className="dd-icon-btn dd-icon-btn-lg"
                aria-label="Chat"
                title={`Chat${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
                onClick={() => setChatOpen(true)}
              >
                <span aria-hidden="true">💬{unreadCount > 0 ? ` ${unreadCount}` : ''}</span>
              </button>
            )}
            <Button
              onClick={() => {
                setOverwhelmIndex(0);
                setOverwhelmMode(true);
              }}
              variant="danger"
              size="sm"
              disabled={!sortedOverwhelmTasks.length}
            >
              Panic
            </Button>
            <UserBadgeBar />
          </div>
        </div>

        <div className="dd-hstack dd-justify-between dd-align-center dd-mb-4">
          <h1 className="dd-heading-xl" style={{ margin: 0 }}>My Tasks</h1>
          <div className="dd-hstack dd-gap-2">
            <button
              className="dd-icon-btn dd-icon-btn-lg"
              aria-label="Toggle view layout"
              title={viewMode === 'detailed' ? 'Switch to compact view' : 'Switch to detailed view'}
              onClick={() => {
                const newMode = viewMode === 'detailed' ? 'compact' : 'detailed';
                setViewMode(newMode);
                localStorage.setItem('tasksViewMode', newMode);
              }}
            >
              <span aria-hidden="true">🗂</span>
            </button>
            <button
              className="dd-icon-btn dd-icon-btn-lg"
              aria-label="Create task"
              title="Create task"
              onClick={() => navigate('/tasks/create')}
              style={{ fontSize: '1.875rem' }}
            >
              <span aria-hidden="true" className="dd-gradient-text">＋</span>
            </button>
          </div>
        </div>

        <ChatSidebar isOpen={chatOpen} onClose={() => setChatOpen(false)} />

        {/* Bulk Edit Bar */}
        {selectedTasks.length > 0 && (
          <div className="dd-bulk-card dd-mb-6">
            <div className="dd-hstack dd-justify-between dd-mb-3 dd-flex-wrap dd-gap-3">
              <h4 className="dd-heading-sm">
                Bulk edit ({selectedTasks.length} selected)
              </h4>
              <Button size="sm" variant="outline" onClick={clearBulkForm}>
                Clear selection
              </Button>
            </div>
            <div className="dd-grid-bulk dd-mb-3">
              <select
                className="dd-select-lg"
                value={bulkCategory}
                onChange={(e) => setBulkCategory(e.target.value)}
              >
                <option value="">Category (apply)</option>
                {allCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <select
                className="dd-select-lg"
                value={bulkProject}
                onChange={(e) => setBulkProject(e.target.value)}
              >
                <option value="">Project (apply)</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <Input
                type="date"
                placeholder="Due date (apply)"
                value={bulkDueDate}
                onChange={(e) => setBulkDueDate(e.target.value)}
              />
              <select
                className="dd-select-lg"
                value={bulkDifficulty}
                onChange={(e) => setBulkDifficulty(e.target.value)}
              >
                <option value="">Difficulty (apply)</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
              <Input
                type="number"
                placeholder="Points (apply)"
                value={bulkPoints}
                onChange={(e) => setBulkPoints(e.target.value)}
              />
              <Input
                placeholder="Tags (comma separated)"
                value={bulkTagsInput}
                onChange={(e) => setBulkTagsInput(e.target.value)}
              />
            </div>
            <Button variant="primary" onClick={applyBulkUpdate}>
              Apply to selected
            </Button>
          </div>
        )}

        {/* Filters Section */}
        <div className="dd-card dd-mb-8">
          <div className={`dd-hstack dd-justify-between ${filtersExpanded ? 'dd-mb-4' : ''}`}>
            <h3 className="dd-heading-md">
              Filters &amp; Sorting
            </h3>
            <Button
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              variant="ghost"
              size="sm"
            >
              {filtersExpanded ? '▲ Collapse' : '▼ Expand'}
            </Button>
          </div>
          {filtersExpanded && (
            <>
              <div className="dd-grid-filters">
            {/* Category Filter */}
            <div>
              <p className="dd-label dd-mb-2">
                Category
              </p>
              <select
                className="dd-select"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Project Filter */}
            <div>
              <p className="dd-label dd-mb-2">
                Project
              </p>
              <select
                className="dd-select"
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
              >
                <option value="">All Projects</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>

            {/* Tag Filter */}
            <div>
              <p className="dd-label dd-mb-2">
                Tag
              </p>
              <select
                className="dd-select"
                value={filterTag}
                onChange={(e) => setFilterTag(e.target.value)}
              >
                <option value="">All Tags</option>
                {allTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <p className="dd-label dd-mb-2">
                Priority
              </p>
              <select
                className="dd-select"
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
              >
                <option value="">All Priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
              </select>
            </div>

            {/* Sort By */}
            <div>
              <p className="dd-label dd-mb-2">
                Sort By
              </p>
              <select
                className="dd-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="priority">Priority</option>
                <option value="created_at">Date Created</option>
                <option value="due_date">Due Date</option>
                <option value="points">Points</option>
                <option value="title">Title</option>
                <option value="category">Category</option>
              </select>
            </div>

            {/* Sort Order */}
            <div>
              <p className="dd-label dd-mb-2">
                Order
              </p>
              <select
                className="dd-select"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="DESC">Descending</option>
                <option value="ASC">Ascending</option>
              </select>
            </div>

            {/* Show Completed */}
            <div>
              <p className="dd-label dd-mb-2">
                Status
              </p>
              <select
                className="dd-select"
                value={showCompleted}
                onChange={(e) => setShowCompleted(e.target.value === 'true')}
              >
                <option value="false">Active Tasks</option>
                <option value="true">Completed Tasks</option>
              </select>
            </div>
              </div>

              <div className="dd-hstack dd-mt-4 dd-justify-end">
                <Button onClick={clearFilters} variant="outline" size="sm">
                  Clear Filters
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="dd-alert-error dd-mb-6">
            <p>{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="dd-text-center" style={{ padding: '5rem 0' }}>
            <div className="dd-spinner dd-spinner-xl"></div>
            <p className="dd-text-lg dd-text-muted dd-mt-4">Loading tasks...</p>
          </div>
        ) : (
          <>
            {/* Tasks Count */}
            <p className="dd-text-lg dd-mb-4 dd-text-muted">
              Showing {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
            </p>

            {/* Tasks List */}
            {tasks.length === 0 ? (
              <div className="dd-alert-info">
                <p className="dd-alert-info-title">
                  No tasks found
                </p>
                <p className="dd-alert-info-text">
                  {showCompleted ? 'You haven\'t completed any tasks yet.' : 'Create your first task to get started!'}
                </p>
                {!showCompleted && (
                  <Button onClick={() => navigate('/tasks/create')} variant="primary" size="lg">
                    Create Your First Task
                  </Button>
                )}
              </div>
            ) : viewMode === 'compact' ? (
              // Compact View - two-column grid with key badges
              <div className="dd-grid-tasks-compact">
                {sortedTasks.map((task) => (
                  <div
                    key={task.id}
                    className={`dd-task-card-compact ${task.is_completed ? 'dd-task-card-compact-completed' : ''}`}
                  >
                    <div className="dd-vstack dd-gap-2">
                      <div className="dd-hstack dd-justify-between dd-align-start dd-gap-3">
                        <div className="dd-vstack dd-gap-1 dd-flex-1">
                          <span
                            className={`dd-task-title-link ${task.is_completed ? 'dd-task-title-completed' : ''}`}
                            onClick={() => focusTask(task)}
                            role="button"
                            title="View task"
                          >
                            {task.title}
                          </span>
                          <div className="dd-hstack dd-gap-2 dd-flex-wrap">
                            {task.project_id && (
                              <Badge variant="info" size="sm">
                                {projects.find(p => p.id === task.project_id)?.name || 'Project'}
                              </Badge>
                            )}
                            <Badge variant={priorityColor(task.priority)} size="sm">
                              {task.priority || 'normal'}
                            </Badge>
                            {task.due_date && (
                              <Badge variant="warning" size="sm">
                                {formatDate(task.due_date)}
                              </Badge>
                            )}
                            {task.tags && task.tags.length > 0 && (
                              <>
                                {task.tags.slice(0, 3).map((tag, idx) => (
                                  <Badge key={idx} variant="secondary" size="sm">
                                    #{tag}
                                  </Badge>
                                ))}
                                {task.tags.length > 3 && (
                                  <Badge variant="secondary" size="sm">
                                    +{task.tags.length - 3}
                                  </Badge>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="dd-vstack dd-gap-1 dd-align-end">
                          <span className="dd-text-sm dd-text-bold dd-text-purple">
                            {task.points} pts
                          </span>
                          {parseInt(task.subtask_count) > 0 && (
                            <Badge variant="secondary" size="sm">⚡ {task.subtask_count} steps</Badge>
                          )}
                          {task.is_completed && (
                            <Badge variant="success" size="sm">✓</Badge>
                          )}
                        </div>
                      </div>

                      <div className="dd-hstack dd-gap-3 dd-justify-end">
                        <input
                          type="checkbox"
                          checked={selectedTasks.includes(task.id)}
                          onChange={() => toggleSelectTask(task.id)}
                          style={{
                            width: '18px',
                            height: '18px',
                            accentColor: 'transparent',
                            border: '2px solid #4a5568',
                            borderRadius: '4px',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            backgroundColor: selectedTasks.includes(task.id) ? '#4a5568' : 'transparent',
                            position: 'relative'
                          }}
                          onFocus={(e) => (e.target.style.boxShadow = '0 0 0 2px rgba(66,153,225,0.6)')}
                          onBlur={(e) => (e.target.style.boxShadow = 'none')}
                        />
                        <div className="dd-hstack dd-gap-4">
                          {!task.is_completed && (
                            <>
                              <button
                                className="dd-icon-btn"
                                aria-label="Complete task"
                                title="Complete task"
                                onClick={() => handleCompleteTask(task.id)}
                              >
                                <span aria-hidden="true" style={{ fontSize: '30px', color: '#15803d' }}>✔</span>
                              </button>
                              <button
                                className="dd-icon-btn"
                                aria-label="AI Breakdown"
                                title="AI Breakdown"
                                onClick={() => handleAIBreakdown(task)}
                              >
                                <span aria-hidden="true" style={{ fontSize: '30px', color: '#f59e0b' }}>⚡</span>
                              </button>
                              <button
                                className="dd-icon-btn"
                                aria-label="Edit task"
                                title="Edit task"
                                onClick={() => handleEditTask(task)}
                              >
                                <span aria-hidden="true" style={{ fontSize: '28px', color: '#2563eb' }}>✎</span>
                              </button>
                            </>
                          )}
                          <button
                            className="dd-icon-btn"
                            aria-label="Delete task"
                            title="Delete task"
                            onClick={() => handleDeleteTask(task.id)}
                          >
                            <span aria-hidden="true" style={{ color: 'red', fontSize: '30px' }}>✖</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Detailed View
              (() => {
                const expandedId = Object.keys(expandedTasks).find(id => expandedTasks[id]);
                const visibleTasks = expandedId
                  ? sortedTasks.filter(t => t.id === parseInt(expandedId))
                  : sortedTasks;
                const isFocused = !!expandedId;
                return (
                  <div className={isFocused && selectedSubtask ? 'dd-tasks-focus-layout' : ''}>
                    <div className="dd-grid-tasks">
                      {visibleTasks.map((task) => (
                  <div
                    key={task.id}
                    className={`dd-task-card ${task.is_completed ? 'dd-task-card-completed' : ''}`}
                  >
                    <div className="dd-vstack dd-gap-2">
                      <div className="dd-hstack dd-justify-between dd-align-start dd-gap-2">
                        <div className="dd-vstack dd-gap-0">
                          <h3
                            className="dd-heading-md"
                            style={{ fontWeight: 'bold', marginBottom: '2px', cursor: 'pointer' }}
                            onClick={() => {
                              if (expandedTasks[task.id]) {
                                setExpandedTasks(prev => ({ ...prev, [task.id]: false }));
                              } else {
                                focusTask(task);
                              }
                            }}
                            title={expandedTasks[task.id] ? 'Collapse' : 'View task'}
                          >
                            {task.title}
                          </h3>
                          <div className="dd-hstack dd-gap-2 dd-flex-wrap" style={{ marginTop: '2px' }}>
                            <Badge variant={statusVariant(task.status)} size="sm">
                              {task.status || 'pending'}
                            </Badge>
                            <Badge variant={priorityColor(task.priority)} size="sm">
                              {task.priority || 'normal'}
                            </Badge>
                            {task.due_date && (
                              <Badge variant="warning" size="sm">
                                Due {formatDate(task.due_date)}
                              </Badge>
                            )}
                            {task.is_completed && (
                              <Badge variant="success" size="sm">✓ Done</Badge>
                            )}
                            {task.is_overdue && (
                              <Badge variant="danger" size="sm">⚠️ Overdue</Badge>
                            )}
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={selectedTasks.includes(task.id)}
                          onChange={() => toggleSelectTask(task.id)}
                          style={{
                            width: '18px',
                            height: '18px',
                            flexShrink: 0,
                            accentColor: 'transparent',
                            border: '2px solid #4a5568',
                            borderRadius: '4px',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            backgroundColor: selectedTasks.includes(task.id) ? '#4a5568' : 'transparent',
                            position: 'relative'
                          }}
                          onFocus={(e) => (e.target.style.boxShadow = '0 0 0 2px rgba(66,153,225,0.6)')}
                          onBlur={(e) => (e.target.style.boxShadow = 'none')}
                        />
                      </div>

                      {task.description && (
                        <p className="dd-text-sm dd-text-muted" style={{ margin: 0 }}>
                          {task.description}
                        </p>
                      )}

                      <div className="dd-hstack dd-gap-2 dd-flex-wrap">
                        <span className="dd-text-sm" style={{ color: '#a0aec0', fontWeight: 'bold' }}>{task.points} pts</span>
                        {task.difficulty && (
                          <Badge variant="primary" size="sm">{task.difficulty}</Badge>
                        )}
                        {getCategoryLabel(task) && (
                          <Badge variant="info" size="sm">{getCategoryLabel(task)}</Badge>
                        )}
                        {task.project_id && (
                          <Badge variant="info" size="sm">
                            {projects.find(p => p.id === task.project_id)?.name || 'Project'}
                          </Badge>
                        )}
                        {task.is_routine && (
                          <Badge variant="info" size="sm">🔄 Routine</Badge>
                        )}
                      </div>

                      {task.tags && task.tags.length > 0 && (
                        <div className="dd-hstack dd-gap-2 dd-flex-wrap">
                          <span className="dd-text-sm" style={{ color: 'var(--dd-purple-500)' }}>Tags:</span>
                          {task.tags.map((tag, idx) => (
                            <Badge key={idx} variant="secondary" size="sm">
                              #{tag}
                            </Badge>
                          ))}
                        </div>
                      )}

                      <hr className="dd-divider" />

                      <div className="dd-vstack dd-gap-2">
                        <Button
                          onClick={() => toggleNotes(task.id)}
                          variant="ghost"
                          size="sm"
                          style={{ alignSelf: 'flex-start' }}
                        >
                          📝 Notes {openNotes[task.id] ? '▼' : '►'}
                        </Button>
                        {openNotes[task.id] && (
                          <div className="dd-vstack dd-gap-2">
                            <div className="dd-hstack dd-gap-2 dd-align-start">
                              <Input
                                placeholder="Add a quick note"
                                value={noteInputs[task.id] || ''}
                                onChange={(e) => setNoteInputs(prev => ({ ...prev, [task.id]: e.target.value }))}
                                size="sm"
                              />
                              <Input
                                placeholder="Attachment URL (optional)"
                                value={noteAttachments[task.id] || ''}
                                onChange={(e) => setNoteAttachments(prev => ({ ...prev, [task.id]: e.target.value }))}
                                size="sm"
                              />
                              <Button
                                onClick={() => handleAddNote(task.id)}
                                variant="primary"
                                size="sm"
                              >
                                Add
                              </Button>
                            </div>
                            {notesLoading[task.id] && (
                              <span className="dd-text-sm dd-text-muted">Loading notes...</span>
                            )}
                            {notesByTask[task.id] && notesByTask[task.id].length > 0 ? (
                              <div className="dd-vstack dd-gap-2">
                                {notesByTask[task.id].map(note => (
                                  <div key={note.id} className="dd-note-item">
                                    <p className="dd-note-content">{note.content}</p>
                                    {note.attachment_url && (
                                      <p className="dd-note-attachment">
                                        Attachment: <a href={note.attachment_url} target="_blank" rel="noreferrer">{note.attachment_url}</a>
                                      </p>
                                    )}
                                    <p className="dd-note-date">
                                      {formatDateTime(note.created_at)}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="dd-text-sm dd-text-muted">No notes yet.</span>
                            )}
                          </div>
                        )}
                      </div>

                      {parseInt(task.subtask_count) > 0 && (
                        <>
                          <hr className="dd-divider" />
                          <div className="dd-vstack dd-gap-2">
                            <button
                              className="dd-icon-btn dd-subtask-toggle"
                              onClick={() => toggleSubtasks(task.id)}
                            >
                              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#7c3aed' }}>
                                {expandedTasks[task.id] ? '▼' : '►'} {task.subtask_count} subtask{task.subtask_count !== '1' ? 's' : ''}
                              </span>
                            </button>
                            {expandedTasks[task.id] && (
                              <div className="dd-subtask-list">
                                {subtasksLoading[task.id] ? (
                                  <span className="dd-text-sm dd-text-muted">Loading subtasks...</span>
                                ) : (
                                  (subtasksByTask[task.id] || []).map((subtask) => (
                                    <div
                                      key={subtask.id}
                                      className={`dd-subtask-item ${subtask.is_completed ? 'dd-subtask-item-done' : ''} ${dragOverSubtask === subtask.id ? 'dd-subtask-drag-over' : ''}`}
                                      draggable
                                      onDragStart={() => handleSubtaskDragStart(subtask)}
                                      onDragOver={(e) => { e.preventDefault(); setDragOverSubtask(subtask.id); }}
                                      onDragLeave={() => setDragOverSubtask(null)}
                                      onDrop={() => handleSubtaskDrop(task.id, subtask)}
                                    >
                                      <span className="dd-subtask-drag-handle" title="Drag to reorder">⠿</span>
                                      <span className="dd-subtask-num">{subtask.sort_order}.</span>
                                      <span
                                        className={`dd-subtask-title dd-subtask-title-link ${subtask.is_completed ? 'dd-subtask-title-done' : ''} ${selectedSubtask?.id === subtask.id ? 'dd-subtask-title-selected' : ''}`}
                                        onClick={() => setSelectedSubtask(selectedSubtask?.id === subtask.id ? null : subtask)}
                                        title="View details"
                                      >
                                        {subtask.title}
                                      </span>
                                      {!subtask.is_completed && (
                                        <button
                                          className="dd-subtask-complete-btn"
                                          title="Complete subtask"
                                          onClick={(e) => { e.stopPropagation(); handleSubtaskComplete(task.id, subtask.id); }}
                                        >
                                          ✔
                                        </button>
                                      )}
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      <hr className="dd-divider" />

                      <div className="dd-hstack dd-gap-3 dd-justify-between dd-flex-wrap">
                        {!task.is_completed && (
                          <>
                            <button
                              className="dd-icon-btn"
                              aria-label="Complete task"
                              title="Complete task"
                              onClick={() => handleCompleteTask(task.id)}
                            >
                              <span aria-hidden="true" style={{ fontSize: '28px', color: '#15803d' }}>✔</span>
                            </button>
                            <button
                              className="dd-icon-btn"
                              aria-label="AI Breakdown"
                              title="AI Breakdown"
                              onClick={() => handleAIBreakdown(task)}
                            >
                              <span aria-hidden="true" style={{ fontSize: '28px', color: '#f59e0b' }}>⚡</span>
                            </button>
                            <button
                              className="dd-icon-btn"
                              aria-label="Edit task"
                              title="Edit task"
                              onClick={() => handleEditTask(task)}
                            >
                              <span aria-hidden="true" style={{ fontSize: '26px', color: '#2563eb' }}>✎</span>
                            </button>
                          </>
                        )}
                        <button
                          className="dd-icon-btn"
                          aria-label="Delete task"
                          title="Delete task"
                          onClick={() => handleDeleteTask(task.id)}
                        >
                          <span aria-hidden="true" style={{ color: 'red', fontSize: '28px' }}>✖</span>
                        </button>
                      </div>
                    </div>
                  </div>
                      ))}
                    </div>

                    {/* Subtask detail panel */}
                    {isFocused && selectedSubtask && (
                      <div className="dd-subtask-detail-panel">
                        <div className="dd-hstack dd-mb-4" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                          <h4 className="dd-heading-md">Step {selectedSubtask.sort_order}</h4>
                          <button
                            className="dd-icon-btn"
                            onClick={() => setSelectedSubtask(null)}
                            title="Close"
                            style={{ fontSize: '1.25rem', color: '#9ca3af' }}
                          >
                            ✕
                          </button>
                        </div>

                        <h3 className="dd-heading-sm dd-mb-4" style={{ color: '#1f2937', lineHeight: 1.4 }}>
                          {selectedSubtask.title}
                        </h3>

                        {selectedSubtask.description && (
                          <div className="dd-subtask-detail-section">
                            <p className="dd-subtask-detail-label">Notes</p>
                            <p className="dd-text-sm" style={{ color: '#374151' }}>{selectedSubtask.description}</p>
                          </div>
                        )}

                        <div className="dd-subtask-detail-section">
                          <p className="dd-subtask-detail-label">Status</p>
                          <div className="dd-hstack dd-gap-2">
                            <Badge variant={selectedSubtask.is_completed ? 'success' : 'secondary'} size="sm">
                              {selectedSubtask.is_completed ? '✓ Done' : 'Pending'}
                            </Badge>
                            <Badge variant="primary" size="sm">{selectedSubtask.difficulty || 'easy'}</Badge>
                            <Badge variant="info" size="sm">{selectedSubtask.points} pts</Badge>
                          </div>
                        </div>

                        <div className="dd-subtask-detail-section">
                          <p className="dd-subtask-detail-label">Due Date</p>
                          <input
                            type="datetime-local"
                            className="dd-input-sm"
                            style={{ width: '100%' }}
                            value={selectedSubtask.due_date ? selectedSubtask.due_date.slice(0, 16) : ''}
                            onChange={(e) => setSelectedSubtask(prev => ({ ...prev, due_date: e.target.value }))}
                            onBlur={async (e) => {
                              const token = localStorage.getItem('token');
                              await fetch(`${API_URL}/api/tasks/${selectedSubtask.id}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                body: JSON.stringify({ due_date: e.target.value || null })
                              });
                              setSubtasksByTask(prev => ({
                                ...prev,
                                [selectedSubtask.parent_task_id]: (prev[selectedSubtask.parent_task_id] || []).map(s =>
                                  s.id === selectedSubtask.id ? { ...s, due_date: e.target.value } : s
                                )
                              }));
                            }}
                          />
                        </div>

                        {!selectedSubtask.is_completed && (
                          <div className="dd-subtask-detail-section">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => {
                                const parentId = selectedSubtask.parent_task_id;
                                handleSubtaskComplete(parentId, selectedSubtask.id);
                                setSelectedSubtask(prev => ({ ...prev, is_completed: true }));
                              }}
                            >
                              ✔ Mark Complete
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()
            )}
          </>
        )}
      </div>

      {overwhelmMode && (
        <div className="dd-overwhelm-overlay">
          <div className="dd-vstack dd-gap-6" style={{ maxWidth: '72rem', width: '100%' }}>
            {currentOverwhelmTask ? (
              <div className="dd-overwhelm-card">
                <div className="dd-vstack dd-gap-3 dd-align-start">
                  <h2 className="dd-heading-lg" style={{ fontSize: '1.875rem' }}>
                    {currentOverwhelmTask.title}
                  </h2>
                  <p className="dd-text-md dd-text-muted">
                    {currentOverwhelmTask.description || 'No description provided.'}
                  </p>
                  <div className="dd-hstack dd-gap-3 dd-flex-wrap">
                    <Badge variant="danger">
                      Priority: {currentOverwhelmTask.priority || 'normal'}
                    </Badge>
                    {currentOverwhelmTask.due_date && (
                      <Badge variant="warning">
                        Due: {formatDate(currentOverwhelmTask.due_date)}
                      </Badge>
                    )}
                    <Badge variant="info">
                      Points: {currentOverwhelmTask.points}
                    </Badge>
                    <Badge variant="success">
                      {getCategoryLabel(currentOverwhelmTask)}
                    </Badge>
                    {currentOverwhelmTask.project && (
                      <Badge variant="primary">
                        Project: {currentOverwhelmTask.project}
                      </Badge>
                    )}
                  </div>
                  {currentOverwhelmTask.tags?.length > 0 && (
                    <div className="dd-hstack dd-gap-2 dd-flex-wrap">
                      {currentOverwhelmTask.tags.map((tag) => (
                        <Badge key={tag} variant="secondary">
                          #{tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="dd-hstack dd-gap-6 dd-pt-2 dd-flex-wrap dd-justify-start">
                    {!currentOverwhelmTask.is_completed && (
                      <>
                        <div className="dd-vstack dd-gap-0 dd-align-center">
                          <button
                            className="dd-icon-btn"
                            aria-label="Complete task"
                            title="Complete task"
                            onClick={() => handleCompleteTask(currentOverwhelmTask.id)}
                          >
                            <span aria-hidden="true" style={{ fontSize: '30px', color: '#15803d' }}>✔</span>
                          </button>
                          <span className="dd-overwhelm-text-light">Complete</span>
                        </div>
                        <div className="dd-vstack dd-gap-0 dd-align-center">
                          <button
                            className="dd-icon-btn"
                            aria-label="AI Breakdown"
                            title="AI Breakdown"
                            onClick={() => handleAIBreakdown(currentOverwhelmTask)}
                          >
                            <span aria-hidden="true" style={{ fontSize: '30px', color: '#f59e0b' }}>⚡</span>
                          </button>
                          <span className="dd-overwhelm-text-light">AI</span>
                        </div>
                        <div className="dd-vstack dd-gap-0 dd-align-center">
                          <button
                            className="dd-icon-btn"
                            aria-label="Edit task"
                            title="Edit task"
                            onClick={() => handleEditTask(currentOverwhelmTask)}
                          >
                            <span aria-hidden="true" style={{ fontSize: '28px', color: '#2563eb' }}>✎</span>
                          </button>
                          <span className="dd-overwhelm-text-light">Edit</span>
                        </div>
                      </>
                    )}
                    <div className="dd-vstack dd-gap-0 dd-align-center">
                      <button
                        className="dd-icon-btn"
                        aria-label="Delete task"
                        title="Delete task"
                        onClick={() => handleDeleteTask(currentOverwhelmTask.id)}
                      >
                        <span aria-hidden="true" style={{ fontSize: '30px', color: 'red' }}>✖</span>
                      </button>
                      <span className="dd-overwhelm-text-light">Delete</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="dd-card dd-text-center" style={{ padding: '2rem' }}>
                <h3 className="dd-heading-md dd-mb-2">No active tasks to show</h3>
                <p className="dd-text-muted">All tasks are completed or unavailable.</p>
              </div>
            )}

            <div className="dd-hstack dd-gap-4 dd-justify-center">
              <Button
                onClick={handleNextOverwhelmTask}
                variant="primary"
                size="lg"
                disabled={sortedOverwhelmTasks.length <= 1}
              >
                Show next task
              </Button>
              <Button
                onClick={() => setOverwhelmMode(false)}
                variant="outline"
                size="lg"
                style={{ backgroundColor: '#e2e8f0', color: '#1a202c' }}
              >
                Exit overwhelm mode
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {editDialogOpen && (
        <div
          className="dd-modal-overlay"
          onClick={() => setEditDialogOpen(false)}
        >
          <div
            className="dd-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="dd-modal-header">
              <h2 className="dd-heading-lg">
                Edit Task
              </h2>
              <Button
                onClick={() => setEditDialogOpen(false)}
                variant="ghost"
                size="sm"
              >
                ✕
              </Button>
            </div>

            {/* Body */}
            <div className="dd-modal-body">
              <div className="dd-vstack dd-gap-4">
                {/* Title */}
                <div>
                  <p className="dd-label dd-mb-2">
                    Task Title *
                  </p>
                  <input
                    className="dd-input"
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                  />
                </div>

                {/* Description */}
                <div>
                  <p className="dd-label dd-mb-2">
                    Description
                  </p>
                  <Textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                    rows={3}
                  />
                </div>

                {/* Points, Difficulty, Priority */}
                <div className="dd-grid-3col">
                  <div>
                    <p className="dd-label dd-mb-2">
                      Points
                    </p>
                    <input
                      className="dd-input"
                      type="number"
                      value={editForm.points}
                      onChange={(e) => setEditForm({...editForm, points: parseInt(e.target.value) || 1})}
                      min="1"
                      max="100"
                    />
                  </div>
                  <div>
                    <p className="dd-label dd-mb-2">
                      Difficulty
                    </p>
                    <select
                      className="dd-select-lg"
                      value={editForm.difficulty}
                      onChange={(e) => setEditForm({...editForm, difficulty: e.target.value})}
                    >
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>
                  <div>
                    <p className="dd-label dd-mb-2">
                      Priority
                    </p>
                    <select
                      className="dd-select-lg"
                      value={editForm.priority || 'normal'}
                      onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                    >
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                    </select>
                  </div>
                </div>

                {/* Category */}
                <div>
                  <p className="dd-label dd-mb-2">
                    Category
                  </p>
                  <select
                    className="dd-select-lg"
                    value={editForm.category_id || ''}
                    onChange={(e) => {
                      const selected = categories.find(c => String(c.id) === e.target.value);
                      setEditForm({
                        ...editForm,
                        category_id: e.target.value,
                        category: selected ? selected.name : ''
                      });
                    }}
                  >
                    <option value="">Choose category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  {categories.length === 0 && (
                    <p className="dd-text-warning-sm">
                      Add categories in Settings first.
                    </p>
                  )}
                </div>

                {/* Status */}
                <div>
                  <p className="dd-label dd-mb-2">
                    Status
                  </p>
                  <select
                    className="dd-select-lg"
                    value={editForm.status || 'pending'}
                    onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                  >
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Project */}
                <div>
                  <p className="dd-label dd-mb-2">
                    Project (Optional)
                  </p>
                  <select
                    className="dd-select-lg"
                    value={editForm.project_id}
                    onChange={(e) => setEditForm({...editForm, project_id: e.target.value})}
                  >
                    <option value="">No Project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Due Date */}
                <div>
                  <p className="dd-label dd-mb-2">
                    Due Date (Optional)
                  </p>
                  <div className="dd-hstack dd-align-center dd-gap-2">
                    <input
                      className="dd-input"
                      type="datetime-local"
                      ref={editDueDateRef}
                      value={editForm.due_date}
                      onChange={(e) => setEditForm({...editForm, due_date: e.target.value})}
                    />
                    <Button
                      onClick={() => editDueDateRef.current?.blur()}
                      variant="outline"
                      size="sm"
                    >
                      OK
                    </Button>
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <p className="dd-label dd-mb-2">
                    Tags (Optional)
                  </p>
                  <div className="dd-hstack dd-gap-2 dd-mb-2">
                    <input
                      className="dd-input-sm"
                      type="text"
                      value={editTagInput}
                      onChange={(e) => setEditTagInput(e.target.value)}
                      onKeyDown={handleEditTagKeyDown}
                      placeholder="Add a tag and press Enter"
                      style={{ flex: 1 }}
                    />
                    <Button
                      onClick={handleAddEditTag}
                      variant="primary"
                      size="sm"
                    >
                      Add
                    </Button>
                  </div>
                  {editForm.tags.length > 0 && (
                    <div className="dd-hstack dd-gap-2 dd-flex-wrap">
                      {editForm.tags.map((tag, idx) => (
                        <span key={idx} className="dd-tag">
                          #{tag}
                          <button
                            className="dd-tag-remove"
                            onClick={() => handleRemoveEditTag(tag)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="dd-modal-footer">
              <Button onClick={() => setEditDialogOpen(false)} variant="outline">
                Cancel
              </Button>
              <Button onClick={handleUpdateTask} variant="primary" size="lg">
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* AI Breakdown Modal */}
      {dialogOpen && selectedTask && (
        <div
          className="dd-modal-overlay"
          onClick={() => {
            setDialogOpen(false);
            setBreakdownResult(null);
            setBreakdownError('');
          }}
        >
          <div
            className="dd-modal dd-modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="dd-modal-header">
              <h2 className="dd-heading-lg">
                AI Task Breakdown Assistant
              </h2>
              <Button
                onClick={() => {
                  setDialogOpen(false);
                  setBreakdownResult(null);
                  setBreakdownError('');
                }}
                variant="ghost"
                size="sm"
              >
                ✕
              </Button>
            </div>

            {/* Body */}
            <div className="dd-modal-body">
              <div className="dd-vstack dd-gap-4">
                <div className="dd-breakdown-info">
                  <h4 className="dd-heading-sm dd-mb-2">
                    {selectedTask.title}
                  </h4>
                  {selectedTask.description && (
                    <p>{selectedTask.description}</p>
                  )}
                </div>

                {breakdownError && (
                  <div className="dd-breakdown-error">
                    <p>{breakdownError}</p>
                  </div>
                )}

                {!breakdownResult && !breakdownLoading && (
                  <Button
                    onClick={() => requestAIBreakdown('initial')}
                    variant="primary"
                    size="lg"
                    className="dd-full-width"
                  >
                    🤖 Generate AI Breakdown
                  </Button>
                )}

                {breakdownLoading && (
                  <div className="dd-text-center" style={{ padding: '2rem 0' }}>
                    <div className="dd-spinner dd-spinner-xl"></div>
                    <p className="dd-text-muted dd-mt-4">AI is analyzing your task...</p>
                  </div>
                )}

                {breakdownResult && (
                  <div className="dd-vstack dd-gap-4">
                    {breakdownResult.summary && (
                      <div className="dd-breakdown-summary">
                        <p className="dd-text-bold dd-mb-2">Summary</p>
                        <p>{breakdownResult.summary}</p>
                      </div>
                    )}

                    {Array.isArray(breakdownResult.sections) && breakdownResult.sections.map((section, idx) => (
                      <div key={idx} className="dd-breakdown-section">
                        <h4 className="dd-heading-sm dd-mb-2">
                          {section.title}
                        </h4>
                        {Array.isArray(section.subtasks) && section.subtasks.map((sub, sidx) => (
                          <div key={sidx} className="dd-mb-3">
                            <p className="dd-text-bold">{sub.title}</p>
                            {Array.isArray(sub.micro_actions) && sub.micro_actions.length > 0 && (
                              <div className="dd-vstack dd-gap-1 dd-mt-1">
                                {sub.micro_actions.map((ma, midx) => (
                                  <div key={midx} className="dd-hstack dd-gap-2">
                                    <span className="dd-text-sm dd-text-muted">•</span>
                                    <span className="dd-text-sm dd-text-muted">{ma}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}

                    {Array.isArray(breakdownResult.adhd_quick_start) && (
                      <div className="dd-breakdown-adhd">
                        <p className="dd-text-bold dd-mb-2">ADHD Quick Start</p>
                        <div className="dd-vstack dd-gap-1">
                          {breakdownResult.adhd_quick_start.map((item, idx) => (
                            <p key={idx}>• {item}</p>
                          ))}
                        </div>
                      </div>
                    )}

                    {breakdownResult.two_min_version && (
                      <div className="dd-breakdown-2min">
                        <p className="dd-text-bold dd-mb-2">2-minute version</p>
                        <p>{breakdownResult.two_min_version}</p>
                      </div>
                    )}

                    <div className="dd-hstack dd-gap-3 dd-pt-2">
                      <Button
                        onClick={() => requestAIBreakdown('further')}
                        variant="outline"
                      >
                        Breakdown Further
                      </Button>
                      <Button
                        onClick={() => requestAIBreakdown('less')}
                        variant="outline"
                      >
                        Breakdown Less
                      </Button>
                      <Button
                        onClick={handleAcceptBreakdown}
                        variant="success"
                        style={{ flex: 1 }}
                        disabled={breakdownLoading}
                      >
                        Accept Breakdown
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="dd-modal-footer">
              <Button onClick={() => {
                setDialogOpen(false);
                setBreakdownResult(null);
                setBreakdownError('');
              }} variant="outline">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Tasks;
